import { useEffect, useRef, useState } from 'react';
import type { WidgetConfig } from './theme.js';

/**
 * The widget's data client.
 *
 * One rule shapes all of this: a widget must not hammer the origin. The API
 * answers with a Cache-Control whose max-age is the correct refresh interval
 * for that data type (10s for prices, 5min for the calendar), so rather than
 * hard-coding a poll interval the widget refreshes on the TTL the server
 * declares. Tuning the cache policy in one place therefore retunes every widget
 * already embedded in the wild, with no redeploy.
 */

export interface Attribution {
  text: string;
  url?: string;
  required: boolean;
}

export interface Envelope<T> {
  data: T;
  as_of: string;
  /** Every provider that contributed to this payload and asks to be credited. */
  attributions: Attribution[];
  ttl: number;
  /**
   * Seconds after `as_of` past which this payload must not be displayed.
   *
   * Set by data types that have no failover peer, where "keep serving the last
   * good copy" is the only outage strategy available and therefore needs an
   * explicit expiry. Absent for data types with standby providers, whose
   * outages are measured in a poll interval rather than in days.
   */
  stale_after?: number | null;
}

export type LoadState<T> =
  | { status: 'loading' }
  | { status: 'ready'; envelope: Envelope<T>; stale: boolean }
  /**
   * Every provider for this data type is down, or the data we hold has aged
   * past its ceiling. Distinct from 'error' because it is not the embedder's
   * fault and there is nothing for them to fix — the widget says so calmly and
   * keeps retrying, rather than showing a red error box on their page.
   */
  | { status: 'unavailable'; message: string; lastGoodAt?: string }
  | { status: 'error'; message: string; envelope?: Envelope<T> };

/** Carries the API's own error code so the caller can branch on it. */
class WidgetApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'WidgetApiError';
  }
}

/**
 * Error codes that mean "the platform has no data right now", as opposed to
 * "this embed is misconfigured". The distinction drives which state the widget
 * shows, and it matters: one of them is our problem and one of them is the
 * broker's.
 */
const UNAVAILABLE_CODES = new Set(['no_source_available', 'unavailable']);

/** Clamped so a bad TTL cannot turn into a request flood or a frozen widget. */
const MIN_REFRESH_MS = 5_000;
const MAX_REFRESH_MS = 15 * 60_000;

export function useWidgetData<T>(
  config: WidgetConfig,
  path: string,
  query: Record<string, string> = {},
): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading' });
  // Retained across refreshes so a failed poll can keep showing the last good
  // data rather than replacing a working widget with an error message.
  const lastGood = useRef<Envelope<T> | undefined>(undefined);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    if (!config.embedKey) {
      setState({ status: 'error', message: 'This widget is missing its embed key.' });
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    // The first request of this page load is the billable impression; the
    // refreshes that follow are not. Marking it lets the API count impressions
    // exactly rather than inferring them from request volume.
    let isFirst = true;

    const tick = async () => {
      try {
        const envelope = await fetchEnvelope<T>(config, path, query, isFirst);
        isFirst = false;
        if (cancelled) return;

        // Past its staleness ceiling the payload stops being "slightly old" and
        // becomes misleading, so it is withdrawn rather than shown. Only a
        // source that declares a ceiling gets this treatment; for the rest,
        // old-but-labelled is better than blank.
        if (isPastCeiling(envelope)) {
          lastGood.current = envelope;
          setState({
            status: 'unavailable',
            message: 'Live data is temporarily unavailable.',
            lastGoodAt: envelope.as_of,
          });
          timer = window.setTimeout(tick, clampRefresh(envelope.ttl * 1000));
          return;
        }

        lastGood.current = envelope;
        setState({ status: 'ready', envelope, stale: false });
        timer = window.setTimeout(tick, clampRefresh(envelope.ttl * 1000));
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Data is unavailable.';
        const unavailable =
          err instanceof WidgetApiError &&
          (UNAVAILABLE_CODES.has(err.code) || err.status >= 500);

        // Keep rendering the last good payload, flagged stale. A broker's page
        // showing slightly old rates beats one showing an error box.
        if (lastGood.current && !isPastCeiling(lastGood.current)) {
          setState({ status: 'ready', envelope: lastGood.current, stale: true });
        } else if (unavailable) {
          setState({
            status: 'unavailable',
            message: 'Live data is temporarily unavailable.',
            lastGoodAt: lastGood.current?.as_of,
          });
        } else {
          setState({ status: 'error', message });
        }

        // Back off after a failure so an origin problem is not made worse by
        // every embedded widget retrying at its normal cadence.
        timer = window.setTimeout(tick, 60_000);
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.embedKey, path, queryKey]);

  return state;
}

async function fetchEnvelope<T>(
  config: WidgetConfig,
  path: string,
  query: Record<string, string>,
  isImpression: boolean,
): Promise<Envelope<T>> {
  const params = new URLSearchParams(query);
  params.set('key', config.embedKey);

  // The parent page's origin. The API cannot read it from Referer, because a
  // request made by this iframe carries the iframe's own URL there — which is
  // always our own domain. See resolveReferrerHost() in the read API.
  if (config.referrer) params.set('ref', config.referrer);
  if (isImpression) params.set('i', '1');

  const response = await fetch(`${path}?${params.toString()}`, {
    // Let the browser's HTTP cache honour the Cache-Control the API sends;
    // that is the layer that keeps a refreshing widget off the origin.
    cache: 'default',
    credentials: 'omit',
  });

  if (!response.ok) {
    const { message, code } = await describeFailure(response);
    throw new WidgetApiError(message, code, response.status);
  }

  return (await response.json()) as Envelope<T>;
}

/**
 * Turn a failure into something a broker can act on.
 *
 * These messages are read by whoever pasted the snippet, usually while
 * wondering why the widget is blank. "Not authorised for this domain" tells
 * them exactly what to fix; "HTTP 403" does not.
 */
async function describeFailure(
  response: Response,
): Promise<{ message: string; code: string }> {
  let code = '';
  try {
    code = ((await response.json()) as { error?: string }).error ?? '';
  } catch {
    // Non-JSON error body, e.g. an error page from a proxy in front of us.
  }

  return { message: messageFor(code, response.status), code };
}

function messageFor(code: string, status: number): string {
  switch (code) {
    case 'domain_not_allowed':
      return 'This domain is not authorised for this embed key.';
    case 'no_allowed_domains':
      return 'No domains have been authorised for this embed key yet.';
    case 'invalid_key':
    case 'missing_key':
      return 'This embed key is not valid.';
    case 'tenant_suspended':
      return 'This account is suspended.';
    case 'rate_limited':
      return 'Too many requests. The widget will retry shortly.';
    case 'quota_exceeded':
      return 'This account has reached its monthly limit.';
    default:
      return status >= 500
        ? 'Market data is temporarily unavailable.'
        : 'This widget could not load its data.';
  }
}

/**
 * Has this payload aged past the ceiling its source declared?
 *
 * Measured against the upstream observation time carried in the envelope, not
 * against when we fetched it: a proxy happily re-serving a week-old copy every
 * ten seconds would otherwise look perpetually fresh.
 */
function isPastCeiling<T>(envelope: Envelope<T>): boolean {
  if (!envelope.stale_after) return false;
  const observed = new Date(envelope.as_of).getTime();
  if (!Number.isFinite(observed)) return false;
  return Date.now() - observed > envelope.stale_after * 1000;
}

function clampRefresh(ms: number): number {
  if (!Number.isFinite(ms)) return 60_000;
  return Math.min(MAX_REFRESH_MS, Math.max(MIN_REFRESH_MS, ms));
}
